const HGM_ATLAS_BASE_URL = "https://atlas.harita.gov.tr/webservis";
let atlasAddressFallbackWarningShown = false;

export const HGM_ATLAS_ATTRIBUTION =
  '&copy; <a href="https://www.harita.gov.tr/" target="_blank" rel="noreferrer">HGM Atlas</a>';

export const HGM_ATLAS_API_KEY = String(
  process.env.REACT_APP_HGM_ATLAS_API_KEY || ""
).trim();

export const isHgmAtlasConfigured = HGM_ATLAS_API_KEY.length > 0;

const ATLAS_LAYER_NAMES = Object.freeze({
  temel: "hgm_harita",
  gece: "hgm_gece",
  siyasi: "hgm_siyasi",
  yukseklik: "hgm_yukseklik",
});

function requireApiKey(apiKey) {
  const normalized = String(apiKey || "").trim();
  if (!normalized) {
    throw new Error("HGM Atlas API anahtarı tanımlı değil.");
  }
  return normalized;
}

export function buildHgmAtlasTileUrl(theme, apiKey = HGM_ATLAS_API_KEY) {
  const layerName = ATLAS_LAYER_NAMES[theme];
  if (!layerName) {
    throw new Error(`Desteklenmeyen HGM Atlas katmanı: ${theme}`);
  }

  const key = requireApiKey(apiKey);
  return `${HGM_ATLAS_BASE_URL}/harita/${layerName}/{z}/{x}/{y}.png?apikey=${encodeURIComponent(key)}`;
}

export function buildHgmAtlasSatelliteUrls(apiKey = HGM_ATLAS_API_KEY) {
  const key = encodeURIComponent(requireApiKey(apiKey));
  return [
    `${HGM_ATLAS_BASE_URL}/ortofoto/{z}/{x}/{y}.jpg?apikey=${key}`,
    `${HGM_ATLAS_BASE_URL}/harita/hgm_ortofoto/{z}/{x}/{y}.png?apikey=${key}`,
  ];
}

export function buildHgmAtlasReverseUrl(lat, lng, apiKey = HGM_ATLAS_API_KEY) {
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    throw new Error("Adres sorgusu için geçerli koordinat gerekli.");
  }

  const key = requireApiKey(apiKey);
  const query = new URLSearchParams({
    lat: String(latitude),
    lng: String(longitude),
    apikey: key,
  });
  return `${HGM_ATLAS_BASE_URL}/arama/reverse?${query.toString()}`;
}

function uniqueAddressParts(parts) {
  const seen = new Set();
  return parts.filter((part) => {
    const value = String(part || "").trim();
    if (!value) return false;
    const key = value.toLocaleLowerCase("tr-TR");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function mapHgmAtlasReverseResponse(data) {
  const feature = Array.isArray(data?.features)
    ? data.features.find((item) => item?.properties)
    : null;
  const properties = feature?.properties || data?.properties || {};
  const il = String(properties.region || properties.province || "").trim();
  const ilce = String(properties.county || properties.district || "").trim();
  const koy = String(
    properties.locality ||
      properties.village ||
      properties.neighbourhood ||
      properties.town ||
      ""
  ).trim();
  const displayName = uniqueAddressParts([
    properties.name,
    koy,
    ilce,
    il,
  ]).join(", ");

  if (!displayName && !il && !ilce && !koy) {
    throw new Error("HGM Atlas adres sonucu bulunamadı.");
  }

  return { displayName: displayName || "Bilinmeyen konum", il, ilce, koy };
}

function mapNominatimReverseResponse(data) {
  const raw = data?.address || {};
  return {
    displayName: String(data?.display_name || "Bilinmeyen konum"),
    il: raw.province || raw.state || "",
    ilce: raw.county || raw.town || raw.city_district || "",
    koy:
      raw.village ||
      raw.hamlet ||
      raw.suburb ||
      raw.locality ||
      raw.neighbourhood ||
      raw.town ||
      raw.quarter ||
      "",
  };
}

async function fetchJson(url, signal) {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Adres servisi HTTP ${response.status} döndürdü.`);
  }
  return response.json();
}

export async function reverseGeocodeLocation(lat, lng, options = {}) {
  const apiKey = String(options.apiKey ?? HGM_ATLAS_API_KEY).trim();

  if (apiKey) {
    try {
      const data = await fetchJson(
        buildHgmAtlasReverseUrl(lat, lng, apiKey),
        options.signal
      );
      return { ...mapHgmAtlasReverseResponse(data), provider: "hgm-atlas" };
    } catch (error) {
      if (options.allowFallback === false) throw error;

      // Kritik saha akışı: Atlas adres ucu geçici olarak kullanılamazsa kayıt
      // tamamlama kesilmez; sorun görünür biçimde loglanıp mevcut servis denenir.
      if (!atlasAddressFallbackWarningShown) {
        console.warn(
          "HGM Atlas ters adres servisi kullanılamadı; mevcut adres servisine dönülüyor.",
          error
        );
        atlasAddressFallbackWarningShown = true;
      }
    }
  }

  const query = new URLSearchParams({
    format: "jsonv2",
    lat: String(lat),
    lon: String(lng),
    zoom: "18",
    addressdetails: "1",
  });
  const data = await fetchJson(
    `https://nominatim.openstreetmap.org/reverse?${query.toString()}`,
    options.signal
  );
  return {
    ...mapNominatimReverseResponse(data),
    provider: apiKey ? "nominatim-fallback" : "nominatim",
  };
}
